import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { IParams } from '../../../../../model/shared/IParams';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { ErrorToast } from '../../../../../utils/toasters';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { IconBook, IconDiscovery } from '../../../../../shared/Icons';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { DiscoveryService } from '../../discovery/service';
import { DiscoveryFavoriteResponse } from '../../discovery/dto/IDiscoveryFilter';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { DashStatsCardSkeleton } from './CampaignDashboardSkeletons';

export function CampaignDiscoveryCard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();
    const [favoriteUsers, setFavoriteUsers] = useState<DiscoveryFavoriteResponse[]>();
    const [info, setInfo] = useState<any>({});
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const fetchFavorites = async () => {
            try {
                const response = await DiscoveryService.getFavoriteUsers({ page: 1, perPage: 4 }, +id!);
                setFavoriteUsers(response.data);
                setTotalCount(response.count);
                setInfo(response?.info);
            } catch (error) {
                ErrorToast(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchFavorites();
    }, [id]);

    const linkToDiscovery = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_DISCOVERY}/${id}`);
    };
    const linkToFavorites = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_DISCOVERY}/${id}/favorites`);
    };
    if (isLoading) {
        return <DashStatsCardSkeleton stats={3} withSubtitle withButton withImages images={4} />;
    }
    return (
        <CardLayout classCard="mb-3">
            <div className="d-flex justify-content-between align-items-top">
                <div className="d-flex cursor-pointer textHover" onClick={linkToDiscovery}>
                    <IconDiscovery width="20" height="20" color={MUTED_TEXT_COLOR} classes={'me-2'} />
                    <div className="d-flex flex-column">
                        <h6 className="d-flex mb-0">{t('brand.campaign.discovery.discovery_and_influencer_list')}</h6>
                        <span className="text-muted">{t('brand.campaign.discovery.discovery_description')}</span>
                    </div>
                </div>
                {!!favoriteUsers?.length && !!info && (
                    <div className="ms-2">
                        <Button onClick={linkToFavorites} className="my-3" variant="outline-primary" size="sm">
                            {t('brand.campaign.discovery.influencer_list')}
                        </Button>
                    </div>
                )}
            </div>
            {!!favoriteUsers?.length && !!info ? (
                <Row className="align-items-center my-3">
                    <Col className="d-flex flex-wrap">
                        <div className="col-6 col-lg px-0">
                            <h3 className="text-primary">{priceSplitter(totalCount ?? 0)}</h3>
                            <div>{t('brand.campaign.discovery.favorites')}</div>
                        </div>
                        <div className="col-6 col-lg px-0">
                            <h3 className="text-primary">{priceSplitter(info?.listTotals?.avgEngagementRate ?? 0)}%</h3>
                            <div>{t('brand.campaign.discovery.engagement_rate')}</div>
                        </div>
                        <div className="col-6 col-lg px-0">
                            <h3 className="text-primary">
                                {numberFormatter(info?.listTotals?.totalAverageViews ?? 0)}
                            </h3>
                            <div>{t('brand.campaign.discovery.average_views')}</div>
                        </div>
                    </Col>
                    <Col md={'auto'} className="d-none d-md-flex justify-content-end">
                        {favoriteUsers?.map(user => {
                            const profile = user?.internalUser || user?.externalUser;

                            if (!profile) {
                                return null;
                            }

                            return (
                                <div key={profile.id} className="d-flex flex-column align-items-center mx-0">
                                    <CustomImageHandler
                                        thumbnailPath={profile.photoUrl}
                                        photoPath={profile.photoUrl}
                                        altTag={profile.title}
                                        classes="dashboard-sm-image ml-2"
                                    />
                                </div>
                            );
                        })}
                    </Col>
                </Row>
            ) : (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <EmptyState
                        desc={t('brand.campaign.discovery.discovery_empty_state')}
                        imgClass="w-40px"
                        iconPath="/icons/emptyBrandCampaignBook.png"
                        addClass="text-center"
                        isHorizontalView={true}
                    />
                    <Button onClick={linkToDiscovery} variant="outline-primary" className="w-md-25">
                        {t('brand.campaign.discovery.discover_now')}
                    </Button>
                </div>
            )}
        </CardLayout>
    );
}
